import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Brand } from '../../components/common/Brand'
import { normalizeError } from '../../lib/errors'
import { initials } from '../../lib/utils'
import { useAuth } from '../providers/auth-context'
import type { NavigationItem, NavigationSection } from './navigation'

function roleLabel(admin: boolean, roles: string[], batchYear: number | null) {
  if (roles.includes('superadmin')) return 'Superadmin'
  if (admin) return batchYear ? `Pentadbir Batch ${batchYear}` : 'Pentadbir'
  return 'Alumni'
}

function SidebarItem({ item, onNavigate }: { item: NavigationItem; onNavigate: () => void }) {
  const Icon = item.icon

  if (item.status === 'coming-soon' || !item.to) {
    return (
      <button
        className="sidebar__item is-disabled"
        type="button"
        disabled
        title={`${item.label} — Akan datang`}
        aria-label={`${item.label} — Akan datang`}
      >
        <Icon aria-hidden="true" />
        <span className="sidebar__item-label">{item.label}</span>
        <span className="sidebar__badge">Akan datang</span>
      </button>
    )
  }

  const destination = item.to

  return (
    <NavLink
      className={({ isActive }) => `sidebar__item${isActive ? ' active' : ''}`}
      to={destination}
      end={item.end}
      title={item.label}
      onClick={onNavigate}
    >
      <Icon aria-hidden="true" />
      <span className="sidebar__item-label">{item.label}</span>
    </NavLink>
  )
}

export function AppSidebar({
  admin,
  collapsed,
  mobileOpen,
  sections,
  onDismissMobile,
  onNavigate,
  onToggleCollapsed,
}: {
  admin: boolean
  collapsed: boolean
  mobileOpen: boolean
  sections: NavigationSection[]
  onDismissMobile: () => void
  onNavigate: () => void
  onToggleCollapsed: () => void
}) {
  const { access, logout } = useAuth()
  const [logoutError, setLogoutError] = useState('')
  const sidebarRef = useRef<HTMLElement>(null)
  const idPrefix = useId().replace(/:/g, '')
  const displayName = access?.displayName ?? (admin ? 'Pentadbiran' : 'Alumni')

  useEffect(() => {
    if (!mobileOpen) return

    const sidebar = sidebarRef.current
    const focusable = sidebar?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )
    focusable?.[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onDismissMobile()
        return
      }
      if (event.key !== 'Tab' || !focusable?.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mobileOpen, onDismissMobile])

  return (
    <aside
      ref={sidebarRef}
      id="app-sidebar"
      className={`sidebar${mobileOpen ? ' is-open' : ''}${collapsed ? ' is-collapsed' : ''}`}
      aria-label={admin ? 'Navigasi pentadbiran' : 'Navigasi alumni'}
    >
      <header className="sidebar__header">
        <Brand
          variant="compact"
          tone="reversed"
          to={admin ? '/admin' : '/app'}
          contextLabel={admin ? 'Pentadbiran' : undefined}
          className="sidebar__brand-full"
          ariaHidden={collapsed}
        />
        <span className="sidebar__brand-symbol">
          <Brand variant="symbol" to={admin ? '/admin' : '/app'} ariaHidden={!collapsed} />
        </span>
        <button
          className="sidebar__collapse"
          type="button"
          aria-label={collapsed ? 'Tunjukkan menu' : 'Sembunyikan menu'}
          aria-expanded={!collapsed}
          onClick={onToggleCollapsed}
        >
          {collapsed ? <ChevronRight aria-hidden="true" /> : <ChevronLeft aria-hidden="true" />}
        </button>
      </header>

      <nav className="sidebar__navigation">
        {sections.map((section, sectionIndex) => {
          const labelId = `${idPrefix}-section-${sectionIndex}`
          return (
            <section className="sidebar__section" key={section.label} aria-labelledby={labelId}>
              <h2 id={labelId}>{section.label}</h2>
              <ul>
                {section.items.map((item) => (
                  <li key={`${section.label}-${item.label}`}>
                    <SidebarItem item={item} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </section>
          )
        })}
      </nav>

      <footer className="sidebar__footer">
        <div className="sidebar__profile">
          <span className="sidebar__avatar" aria-hidden="true">
            {initials(displayName) || 'SA'}
          </span>
          <span className="sidebar__profile-copy">
            <strong>{displayName}</strong>
            <small>{roleLabel(admin, access?.roles ?? [], access?.adminBatchYear ?? null)}</small>
          </span>
        </div>
        {logoutError ? (
          <p className="sidebar__error" role="alert">
            {logoutError}
          </p>
        ) : null}
        <button
          className="sidebar__logout"
          type="button"
          title="Log keluar"
          onClick={() => {
            setLogoutError('')
            void logout().catch((error) => setLogoutError(normalizeError(error).message))
          }}
        >
          <LogOut aria-hidden="true" />
          <span>Log keluar</span>
        </button>
      </footer>
    </aside>
  )
}
